import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import RiskGauge from "@/components/RiskGauge";
import { VerdictBadge, SeverityBadge } from "@/components/RiskBadge";
import {
  ArrowLeft, Cpu, Package, Puzzle, EyeOff, Network, Mic, Video, User, Monitor, AlertTriangle,
  FileJson, FileText, RefreshCw, Copy, Check, PlayCircle, GitCompareArrows, TrendingUp, TrendingDown,
} from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CAT_LABELS = {
  cheating_tool: "AI Cheating Tool",
  ai_assistant: "AI Assistant",
  remote_tool: "Remote Control",
  installed_program: "Installed (not running)",
  browser_extension: "Browser Extension",
  overlay_window: "Hidden Overlay",
  virtual_audio: "Virtual Audio Device",
  virtual_camera: "Virtual Camera",
  network_connection: "Live AI Server Connection",
};

function Section({ icon: Icon, title, count, children, testid }) {
  return (
    <div className="card-soc overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/8">
        <div className="flex items-center gap-2.5">
          <Icon className="w-4.5 h-4.5 text-cyan-400" style={{ width: 18, height: 18 }} />
          <h3 className="font-head font-semibold text-sm">{title}</h3>
        </div>
        <span className="font-mono text-xs text-slate-500">{count}</span>
      </div>
      <div className="p-4 max-h-72 overflow-y-auto">{children}</div>
    </div>
  );
}

const listItem = "text-xs font-mono text-slate-400 py-1 border-b border-white/5 last:border-0";

export default function ScanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scan, setScan] = useState(null);
  const [err, setErr] = useState("");
  const [rescan, setRescan] = useState(null);
  const [rescanLoading, setRescanLoading] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    setScan(null);
    setRescan(null);
    api.get(`/scans/${id}`).then(({ data }) => setScan(data)).catch(() => setErr("Scan not found"));
  }, [id]);

  const requestRescan = async () => {
    setRescanLoading(true);
    try {
      const { data } = await api.post(`/scans/${id}/rescan`);
      setRescan(data);
    } finally {
      setRescanLoading(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  if (err) return <div className="text-slate-400">{err}</div>;
  if (!scan) return <div className="text-slate-500">Loading…</div>;

  const rescanLink = rescan ? `${window.location.origin}/scan/${rescan.token}` : "";
  const ch = scan.changes;
  // Clients never see the raw process / installed-program / network inventory.
  const showRawInventory = user?.role === "admin";

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate("/scans")}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200"
        >
          <ArrowLeft className="w-4 h-4" /> Back to results
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={requestRescan}
            disabled={rescanLoading}
            data-testid="request-rescan-button"
            className="flex items-center gap-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 rounded-lg px-3.5 py-2 text-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${rescanLoading ? "animate-spin" : ""}`} /> Request Re-scan
          </button>
          <a
            href={`${API}/scans/${scan.id}/report.pdf`}
            data-testid="download-pdf-button"
            className="flex items-center gap-2 bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/30 text-cyan-300 rounded-lg px-3.5 py-2 text-sm"
          >
            <FileText className="w-4 h-4" /> Export PDF
          </a>
          <a
            href={`${API}/scans/${scan.id}/report.json`}
            data-testid="download-json-button"
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-lg px-3.5 py-2 text-sm"
          >
            <FileJson className="w-4 h-4" /> Export JSON
          </a>
        </div>
      </div>

      {/* Re-scan invite link (after requesting a mid-interview re-scan) */}
      {rescan && (
        <div className="card-soc p-5 mb-5 fade-up" style={{ borderColor: "rgba(245,158,11,0.35)" }} data-testid="rescan-link-card">
          <div className="flex items-center gap-2 mb-3">
            <RefreshCw className="w-4 h-4 text-amber-400" />
            <span className="font-head font-semibold text-sm">Re-scan requested — send this one-time link to the candidate now</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="term flex-1 px-3 py-2.5 text-amber-300 truncate text-xs" data-testid="rescan-link-value">{rescanLink}</code>
            <button onClick={() => copy(rescanLink, "resc")} data-testid="copy-rescan-link-button" className="p-2.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 shrink-0">
              {copied === "resc" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
            <a href={rescanLink} data-testid="run-rescan-demo-link" className="flex items-center gap-2 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 rounded-lg px-3.5 py-2.5 text-sm shrink-0">
              <PlayCircle className="w-4 h-4" /> Candidate Page
            </a>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            The re-scan result will be automatically compared against this scan and flagged if the environment changed.
            Valid for {user?.role === "client" ? "2 hours" : "24 hours"}, one-time use.
          </p>
        </div>
      )}

      {/* Environment change panel (this scan is itself a re-scan) */}
      {scan.is_rescan && ch && (
        <div
          className="card-soc p-5 mb-5 fade-up"
          data-testid="environment-change-panel"
          style={{ borderColor: ch.changed ? "rgba(239,68,68,0.4)" : "rgba(16,185,129,0.35)" }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <GitCompareArrows className="w-5 h-5" style={{ color: ch.changed ? "#EF4444" : "#10B981" }} />
              <h3 className="font-head font-bold">
                {ch.changed ? "Environment Changed Since Baseline" : "No Environment Change vs Baseline"}
              </h3>
            </div>
            <button onClick={() => navigate(`/scans/${ch.baseline_scan_id}`)} className="text-sm text-cyan-400 hover:text-cyan-300">
              View baseline scan →
            </button>
          </div>
          <div className="flex items-center gap-3 mb-4 text-sm">
            <span className="text-slate-400">Baseline:</span>
            <VerdictBadge verdict={ch.baseline_verdict} />
            <span className="text-slate-500 font-mono">{ch.baseline_score}</span>
            <span className="text-slate-500">→ Now:</span>
            <VerdictBadge verdict={scan.verdict} />
            <span className="text-slate-500 font-mono">{scan.risk_score}</span>
            {ch.risk_delta !== 0 && (
              <span className={`flex items-center gap-1 font-mono text-xs ${ch.risk_delta > 0 ? "text-red-400" : "text-emerald-400"}`}>
                {ch.risk_delta > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {ch.risk_delta > 0 ? "+" : ""}{ch.risk_delta}
              </span>
            )}
          </div>
          {ch.changed ? (
            <div className="grid md:grid-cols-2 gap-4">
              <DiffList title="New threats detected" items={ch.new_detections} tone="red" />
              <DiffList title="New hidden overlays" items={ch.new_overlays} tone="red" />
              <DiffList title="New virtual audio devices" items={ch.new_audio_devices} tone="red" />
              <DiffList title="New virtual cameras" items={ch.new_camera_devices} tone="amber" />
              <DiffList title="New browser extensions" items={ch.new_extensions} tone="amber" />
              <DiffList title="Newly started processes" items={ch.new_processes} tone="slate" />
              <DiffList title="Resolved threats" items={ch.resolved_detections} tone="green" />
            </div>
          ) : (
            <p className="text-sm text-emerald-400">
              The candidate's environment is consistent with the baseline scan. No new cheating tools, overlays or
              devices appeared.
            </p>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5 mb-5">
        <div className="card-soc p-6 flex flex-col items-center justify-center">
          <RiskGauge score={scan.risk_score} verdict={scan.verdict} />
          <div className="mt-4"><VerdictBadge verdict={scan.verdict} testid="risk-score-badge" /></div>
        </div>

        <div className="lg:col-span-2 card-soc p-6">
          <p className="eyebrow mb-3">CANDIDATE SNAPSHOT</p>
          <div className="flex items-center gap-3 mb-4">
            <h1 className="font-head text-2xl font-extrabold tracking-tight">{scan.candidate_name}</h1>
            {scan.is_rescan && (
              <span className="font-mono text-[10px] font-semibold px-2 py-1 rounded tracking-wider text-amber-300 bg-amber-500/12 border border-amber-500/30">
                RE-SCAN
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Info icon={User} label="Email" value={scan.candidate_email || "—"} />
            <Info icon={Monitor} label="Interview" value={scan.interview_id || "—"} />
            <Info icon={Cpu} label="Hostname" value={scan.hostname || "—"} />
            <Info icon={Monitor} label="Operating System" value={`${scan.os_name || "?"} ${scan.os_version || ""}`} />
            <Info icon={User} label="Local User" value={scan.username || "—"} />
            <Info icon={AlertTriangle} label="Scan Duration" value={`${scan.scan_duration_ms || 0} ms`} />
          </div>
        </div>
      </div>

      {/* Detections */}
      <div className="card-soc mb-5 overflow-hidden" data-testid="detected-tools-card">
        <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-white/8">
          <AlertTriangle className="w-4.5 h-4.5 text-red-400" style={{ width: 18, height: 18 }} />
          <h3 className="font-head font-semibold">Detections ({scan.detections?.length ?? 0})</h3>
          <span className="eyebrow ml-2">HIGH / MEDIUM = ACTIVE THREAT · LOW = INFORMATIONAL</span>
        </div>
        {(!scan.detections || scan.detections.length === 0) ? (
          <div className="px-5 py-8 text-center text-emerald-400 text-sm">
            No cheating tools, overlays or virtual devices detected. Environment is clean.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {scan.detections.map((d, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3" data-testid={`detection-${i}`}>
                <div className="min-w-0">
                  <div className="text-sm text-slate-100 font-medium truncate">{d.name}</div>
                  <div className="text-xs text-slate-500 font-mono truncate">
                    {CAT_LABELS[d.category] || d.category} · {d.evidence}
                  </div>
                </div>
                <SeverityBadge severity={d.severity} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Inventory grid */}
      {!showRawInventory && (
        <div className="card-soc p-4 mb-5 text-sm text-slate-400 flex items-center gap-2.5" data-testid="inventory-restricted-note">
          <Cpu className="w-4 h-4 text-slate-500" />
          Running processes, installed programs and network connections are managed by your administrator and are not
          shown in the client view. Detected AI tools, hidden overlays and virtual devices remain fully visible below.
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-5">
        {showRawInventory && (
          <Section icon={Cpu} title="Running Processes" count={scan.processes?.length ?? 0} testid="section-processes">
            {(scan.processes || []).slice(0, 60).map((p, i) => (
              <div key={i} className={listItem}>{p.name} <span className="text-slate-600">#{p.pid}</span></div>
            ))}
          </Section>
        )}
        {showRawInventory && (
          <Section icon={Package} title="Installed Programs" count={scan.installed_programs?.length ?? 0} testid="section-installed">
            {(scan.installed_programs || []).slice(0, 80).map((p, i) => (
              <div key={i} className={listItem}>{p}</div>
            ))}
          </Section>
        )}
        <Section icon={Puzzle} title="Browser Extensions" count={scan.browser_extensions?.length ?? 0} testid="section-extensions">
          {(scan.browser_extensions || []).length === 0 ? (
            <div className="text-xs text-slate-600">None found.</div>
          ) : (
            (scan.browser_extensions || []).map((p, i) => <div key={i} className={listItem}>{p}</div>)
          )}
        </Section>
        <Section icon={EyeOff} title="Hidden Overlay Windows" count={scan.overlay_windows?.length ?? 0} testid="section-overlays">
          {(scan.overlay_windows || []).length === 0 ? (
            <div className="text-xs text-slate-600">No stealth overlays detected.</div>
          ) : (
            (scan.overlay_windows || []).map((w, i) => (
              <div key={i} className={listItem}>
                <span className="text-red-400">{w.title}</span> — {w.desc}
              </div>
            ))
          )}
        </Section>
        {showRawInventory && (
          <Section icon={Network} title="Network Connections" count={scan.network_connections?.length ?? 0} testid="section-network">
            {(scan.network_connections || []).slice(0, 80).map((c, i) => (
              <div key={i} className={`${listItem} ${c.tool ? "text-red-400" : ""}`}>
                {c.raddr} <span className="text-slate-600">{c.status}</span>
                {c.process && <span className="text-slate-500"> · {c.process}</span>}
                {c.domain && <span className="text-red-400"> · {c.domain}</span>}
              </div>
            ))}
          </Section>
        )}
        <div className="space-y-5">
          <Section icon={Mic} title="Virtual Audio Devices" count={scan.audio_devices?.length ?? 0} testid="section-audio">
            {(scan.audio_devices || []).length === 0 ? (
              <div className="text-xs text-slate-600">None found.</div>
            ) : (
              (scan.audio_devices || []).map((a, i) => <div key={i} className={`${listItem} text-red-400`}>{a}</div>)
            )}
          </Section>
          <Section icon={Video} title="Virtual Camera Devices" count={scan.camera_devices?.length ?? 0} testid="section-camera">
            {(scan.camera_devices || []).length === 0 ? (
              <div className="text-xs text-slate-600">None found.</div>
            ) : (
              (scan.camera_devices || []).map((a, i) => <div key={i} className={`${listItem} text-amber-400`}>{a}</div>)
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-slate-500 mt-0.5" />
      <div className="min-w-0">
        <div className="eyebrow">{label}</div>
        <div className="text-slate-200 truncate">{value}</div>
      </div>
    </div>
  );
}

const TONE = {
  red: "text-red-400",
  amber: "text-amber-400",
  green: "text-emerald-400",
  slate: "text-slate-400",
};

function DiffList({ title, items, tone }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-[#0B1220] border border-white/8 rounded-lg p-3">
      <div className="eyebrow mb-2">{title} ({items.length})</div>
      <div className="space-y-1 max-h-32 overflow-y-auto">
        {items.slice(0, 40).map((it, i) => (
          <div key={i} className={`text-xs font-mono ${TONE[tone] || "text-slate-400"}`}>
            {String(it).replace(/^[a-z_]+::/, "")}
          </div>
        ))}
      </div>
    </div>
  );
}
